import React, { useState } from 'react';
import { Network, Wrench, Radio, Mail, Phone, MapPin, CheckCircle2, ChevronRight, Building2, MapPinned } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { mockData } from '../mock';
import { toast } from 'sonner';
import LogoMRTPlus from '../components/LogoMRTPlus';
import LogoRTBusiness from '../components/LogoRTBusiness';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  Network: Network,
  Wrench: Wrench,
  Radio: Radio
};

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    society: 'general',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast.success(response.data.message);
        setFormData({ name: '', email: '', phone: '', company: '', society: 'general', message: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.response?.data?.detail || 'Une erreur est survenue. Veuillez réessayer.');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (value) => {
    setFormData({ ...formData, society: value });
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-sm">
        <nav className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <LogoRTBusiness className="h-14 w-auto" />
              <div className="flex flex-col">
                <span className="text-xl font-bold bg-gradient-to-r from-[#C8247C] via-[#8B1F5C] to-[#2a2a2a] bg-clip-text text-transparent">
                  RT BUSINESS
                </span>
                <span className="text-xs text-gray-600">Groupe Télécoms</span>
              </div>
            </div>
            
            {/* Bouton Contact Mobile - Toujours visible */}
            <Button 
              onClick={() => scrollToSection('contact')}
              size="lg"
              className="md:hidden bg-gradient-to-r from-[#C8247C] to-[#D9006C] hover:from-[#A6387F] hover:to-[#C8247C] text-white shadow-lg shadow-pink-500/30 transition-all duration-300 hover:scale-105 font-semibold"
            >
              <Mail className="h-5 w-5" />
            </Button>
            
            {/* Navigation Desktop */}
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-[#C8247C] transition-colors font-medium">
                Accueil
              </button>
              <button onClick={() => scrollToSection('rtconnect')} className="text-gray-700 hover:text-[#C8247C] transition-colors font-medium">
                RT CONNECT
              </button>
              <button onClick={() => scrollToSection('mrtplus')} className="text-gray-700 hover:text-[#1a1a1a] transition-colors font-medium">
                MRT+
              </button>
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-[#C8247C] transition-colors font-medium">
                Services
              </button>
              <button onClick={() => scrollToSection('clients')} className="text-gray-700 hover:text-[#C8247C] transition-colors font-medium">
                Nos Clients
              </button>
              <Button 
                onClick={() => scrollToSection('contact')}
                size="lg"
                className="bg-gradient-to-r from-[#C8247C] to-[#D9006C] hover:from-[#A6387F] hover:to-[#C8247C] text-white shadow-lg shadow-pink-500/30 transition-all duration-300 hover:scale-105 font-semibold px-8"
              >
                <Mail className="mr-2 h-5 w-5" />
                Contact
              </Button>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section - RT BUSINESS */}
      <section id="home" className="pt-32 pb-20 relative overflow-hidden min-h-screen">
        {/* Background image avec overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_fiber-solutions-rt/artifacts/nt7qdipn_photo-1597733336794-12d05021d510.jpeg')`,
            opacity: 0.15
          }}
        ></div>
        
        {/* Gradient overlay pour meilleur contraste */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-white/90 to-[#C8247C]/5"></div>
        
        {/* Effet de particules/glow */}
        <div className="absolute inset-0 bg-gradient-radial from-[#C8247C]/10 via-transparent to-transparent"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="space-y-8 animate-fadeIn">
              <div className="inline-block">
                <span className="px-4 py-2 bg-gradient-to-r from-[#C8247C]/10 to-[#1a1a1a]/10 text-[#C8247C] rounded-full text-sm font-semibold border border-[#C8247C]/20">
                  {mockData.holding.tagline}
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                {mockData.hero.title}
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                {mockData.hero.description}
              </p>
              
              {/* Two Company Logos */}
              <div className="flex flex-wrap gap-8 items-center justify-center pt-4">
                <div className="space-y-2">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_fiber-solutions-rt/artifacts/yovt1gja_LOGO%20RT%20CONNECT%20%281%29.png" 
                    alt="RT CONNECT" 
                    className="h-16 w-auto cursor-pointer hover:scale-105 transition-transform"
                    onClick={() => scrollToSection('rtconnect')}
                  />
                </div>
                <div className="w-px h-16 bg-gray-300"></div>
                <div className="cursor-pointer hover:scale-105 transition-transform" onClick={() => scrollToSection('mrtplus')}>
                  <LogoMRTPlus className="h-16 w-auto" />
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4 justify-center">
                <Button 
                  onClick={() => scrollToSection('services')}
                  size="lg" 
                  className="bg-gradient-to-r from-[#C8247C] to-[#D9006C] hover:from-[#A6387F] hover:to-[#C8247C] text-white shadow-lg shadow-pink-500/30 transition-all duration-300 hover:scale-105"
                >
                  {mockData.hero.cta}
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  onClick={() => scrollToSection('contact')}
                  size="lg" 
                  variant="outline"
                  className="border-2 border-gray-800 text-gray-800 hover:bg-gray-800/10 transition-all duration-300"
                >
                  Nous Contacter
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RT CONNECT Section */}
      <section id="rtconnect" className="py-20 bg-gradient-to-br from-[#C8247C]/5 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_fiber-solutions-rt/artifacts/yovt1gja_LOGO%20RT%20CONNECT%20%281%29.png" 
                  alt="RT CONNECT" 
                  className="h-20 w-auto"
                />
                <h2 className="text-4xl font-bold text-gray-900">
                  {mockData.rtconnect.name}
                </h2>
                <p className="text-xl text-[#C8247C] font-semibold italic">
                  {mockData.rtconnect.tagline}
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {mockData.rtconnect.description}
                </p>
                <div className="bg-gradient-to-r from-[#C8247C]/10 to-[#6A009B]/10 p-4 rounded-xl border-l-4 border-[#C8247C]">
                  <div className="flex items-start space-x-3">
                    <MapPin className="h-5 w-5 text-[#C8247C] flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">Zones d'intervention</p>
                      <p className="text-gray-700">{mockData.rtconnect.zones}</p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col space-y-3 pt-4">
                  <div className="flex items-center space-x-3 text-gray-700">
                    <Mail className="h-5 w-5 text-[#C8247C]" />
                    <span>{mockData.rtconnect.email}</span>
                  </div>
                </div>
                <Button 
                  onClick={() => scrollToSection('contact')}
                  className="bg-gradient-to-r from-[#C8247C] to-[#D9006C] hover:from-[#A6387F] hover:to-[#C8247C] text-white"
                >
                  Contacter RT CONNECT
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#C8247C]/30 to-[#6A009B]/30 rounded-2xl blur-2xl"></div>
                <img 
                  src="https://images.unsplash.com/photo-1594915854088-2128db6a8db5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxmaWJlciUyMG9wdGljJTIwbmV0d29ya3xlbnwwfHx8fDE3NzM0MTkwODN8MA&ixlib=rb-4.1.0&q=85" 
                  alt="RT CONNECT Services" 
                  className="relative rounded-2xl shadow-xl w-full h-[400px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MRT+ Section */}
      <section id="mrtplus" className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="relative order-2 lg:order-1">
                <div className="absolute inset-0 bg-gradient-to-br from-gray-400/30 to-gray-600/30 rounded-2xl blur-2xl"></div>
                <img 
                  src="https://images.unsplash.com/photo-1594915440248-1e419eba6611?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxmaWJlciUyMG9wdGljJTIwbmV0d29ya3xlbnwwfHx8fDE3NzM0MTkwODN8MA&ixlib=rb-4.1.0&q=85" 
                  alt="MRT+ Services" 
                  className="relative rounded-2xl shadow-xl w-full h-[400px] object-cover"
                />
              </div>
              <div className="space-y-6 order-1 lg:order-2">
                <LogoMRTPlus className="h-20 w-auto" />
                <h2 className="text-4xl font-bold text-gray-900">
                  {mockData.mrtplus.name}
                </h2>
                <p className="text-xl text-gray-700 font-semibold italic">
                  {mockData.mrtplus.tagline}
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {mockData.mrtplus.description}
                </p>
                <div className="bg-gradient-to-r from-gray-100 to-gray-200 p-4 rounded-xl border-l-4 border-gray-700">
                  <div className="flex items-start space-x-3">
                    <MapPin className="h-5 w-5 text-gray-700 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">Zones d'intervention</p>
                      <p className="text-gray-700">{mockData.mrtplus.zones}</p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col space-y-3 pt-4">
                  <div className="flex items-center space-x-3 text-gray-700">
                    <Mail className="h-5 w-5 text-gray-700" />
                    <span>{mockData.mrtplus.email}</span>
                  </div>
                </div>
                <Button 
                  onClick={() => scrollToSection('contact')}
                  className="bg-gradient-to-r from-gray-800 to-gray-600 hover:from-gray-700 hover:to-gray-500 text-white"
                >
                  Contacter MRT+
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
              Nos Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Des solutions complètes pour tous vos besoins en réseaux fibre optique
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockData.services.map((service, index) => {
              const IconComponent = iconMap[service.icon];
              return (
                <Card 
                  key={service.id} 
                  className="group hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-[#C8247C]/30 overflow-hidden bg-white"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a]/80 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg">
                        <IconComponent className="h-6 w-6 text-[#C8247C]" />
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-6 space-y-4">
                    <h3 className="text-2xl font-bold text-gray-900 group-hover:text-[#C8247C] transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {service.description}
                    </p>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-sm text-gray-700">
                          <CheckCircle2 className="h-5 w-5 text-[#C8247C] flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section id="clients" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
              Nos Clients
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Ils nous font confiance pour leurs infrastructures réseau
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-12 max-w-5xl mx-auto">
            {mockData.clients.map((client) => (
              <Card 
                key={client.id} 
                className="group hover:shadow-2xl transition-all duration-300 bg-white border-2 border-gray-100 hover:border-[#C8247C]/30 p-8"
              >
                <div className="flex flex-col items-center space-y-6">
                  <div className="w-full h-32 flex items-center justify-center p-4 bg-white rounded-xl">
                    <img 
                      src={client.logo} 
                      alt={client.name}
                      className="max-w-full max-h-full object-contain grayscale group-hover:grayscale-0 transition-all duration-300"
                    />
                  </div>
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{client.name}</h3>
                    <p className="text-sm text-gray-600">{client.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div>
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                  {mockData.about.title}
                </h2>
                <p className="text-xl text-[#C8247C] font-semibold mb-6">
                  {mockData.about.subtitle}
                </p>
              </div>
              <p className="text-lg text-gray-600 leading-relaxed">
                {mockData.about.description}
              </p>
              <p className="text-lg text-gray-700 leading-relaxed font-medium bg-gradient-to-r from-[#C8247C]/10 to-[#1a1a1a]/10 p-6 rounded-xl border-l-4 border-[#C8247C]">
                {mockData.about.mission}
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {mockData.about.values.map((value, index) => (
                  <div key={index} className="space-y-2">
                    <h4 className="font-bold text-gray-900">{value.title}</h4>
                    <p className="text-sm text-gray-600">{value.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {mockData.about.stats.map((stat, index) => (
                <Card 
                  key={index} 
                  className="text-center p-8 bg-gradient-to-br from-[#C8247C] to-[#1a1a1a] text-white border-0 shadow-xl hover:scale-105 transition-transform duration-300"
                >
                  <div className="text-4xl font-bold mb-2">{stat.number}</div>
                  <div className="text-sm opacity-90">{stat.label}</div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12 space-y-4">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
                {mockData.contact.title}
              </h2>
              <p className="text-xl text-gray-600">
                {mockData.contact.description}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              <Card className="text-center p-6 hover:shadow-lg transition-shadow border-2 border-gray-100">
                <div className="w-12 h-12 bg-gradient-to-r from-[#C8247C] to-[#D9006C] rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Email</h3>
                <a href={`mailto:${mockData.holding.email}`} className="text-[#C8247C] hover:underline text-sm">
                  {mockData.holding.email}
                </a>
              </Card>
              
              <Card className="text-center p-6 hover:shadow-lg transition-shadow border-2 border-gray-100">
                <div className="w-12 h-12 bg-gradient-to-r from-[#C8247C] to-[#D9006C] rounded-xl flex items-center justify-center mx-auto mb-4">
                  <MapPin className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Adresse</h3>
                <p className="text-gray-600 text-sm">{mockData.holding.address}</p>
              </Card>
            </div>

            <Card className="p-8 shadow-xl border-2 border-gray-100">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">
                      {mockData.contact.formFields.name} *
                    </label>
                    <Input 
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="border-2 border-gray-200 focus:border-[#C8247C] transition-colors"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">
                      {mockData.contact.formFields.email} *
                    </label>
                    <Input 
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="border-2 border-gray-200 focus:border-[#C8247C] transition-colors"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">
                      {mockData.contact.formFields.phone}
                    </label>
                    <Input 
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="border-2 border-gray-200 focus:border-[#C8247C] transition-colors"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">
                      {mockData.contact.formFields.company}
                    </label>
                    <Input 
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="border-2 border-gray-200 focus:border-[#C8247C] transition-colors"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">
                    {mockData.contact.formFields.society} *
                  </label>
                  <Select value={formData.society} onValueChange={handleSelectChange}>
                    <SelectTrigger className="border-2 border-gray-200 focus:border-[#C8247C]">
                      <SelectValue placeholder="Sélectionnez une société" />
                    </SelectTrigger>
                    <SelectContent>
                      {mockData.contact.societies.map((soc) => (
                        <SelectItem key={soc.value} value={soc.value}>
                          {soc.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">
                    {mockData.contact.formFields.message} *
                  </label>
                  <Textarea 
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="border-2 border-gray-200 focus:border-[#C8247C] transition-colors resize-none"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  size="lg"
                  className="w-full bg-gradient-to-r from-[#C8247C] to-[#D9006C] hover:from-[#A6387F] hover:to-[#C8247C] text-white shadow-lg shadow-pink-500/30 transition-all duration-300 hover:scale-[1.02]"
                >
                  {mockData.contact.formFields.submit}
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
              </form>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="text-2xl font-bold mb-4">RT BUSINESS</div>
              <p className="text-gray-300 text-sm">
                {mockData.holding.tagline}
              </p>
            </div>
            
            <div>
              <h3 className="font-bold text-lg mb-4">Nos Sociétés</h3>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => scrollToSection('rtconnect')} className="text-gray-300 hover:text-[#C8247C] transition-colors">
                    RT CONNECT
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('mrtplus')} className="text-gray-300 hover:text-white transition-colors">
                    MRT+
                  </button>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-bold text-lg mb-4">Navigation</h3>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-gray-300 hover:text-[#C8247C] transition-colors">
                    Services
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('clients')} className="text-gray-300 hover:text-[#C8247C] transition-colors">
                    Nos Clients
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('contact')} className="text-gray-300 hover:text-[#C8247C] transition-colors">
                    Contact
                  </button>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-bold text-lg mb-4">Contact</h3>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-[#C8247C]" />
                  <span className="text-sm">{mockData.holding.email}</span>
                </li>
                <li className="flex items-start space-x-2">
                  <MapPin className="h-4 w-4 text-[#C8247C] flex-shrink-0 mt-1" />
                  <span className="text-sm">{mockData.holding.address}</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; {new Date().getFullYear()} RT BUSINESS. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
